import { useState, useMemo } from "react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import SEO from "@/components/SEO";
import BreadcrumbSchema from "@/components/BreadcrumbSchema";
import CalendarioMensile from "@/components/calendario/CalendarioMensile";
import CardOrtaggio from "@/components/calendario/CardOrtaggio";
import FiltriOrtaggi from "@/components/calendario/FiltriOrtaggi";
import GuidaMensile from "@/components/calendario/GuidaMensile";
import { ortaggi } from "@/data/ortaggi";
import { getMoonPhase } from "@/utils/moonPhase";

const CalendarioSemine = () => {
  const currentDate = new Date();
  const [selectedMonth, setSelectedMonth] = useState(currentDate.getMonth());
  const [selectedYear, setSelectedYear] = useState(currentDate.getFullYear());
  const [zona, setZona] = useState<"nord" | "centro" | "sud">("centro");

  // Filtri
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedType, setSelectedType] = useState("tutti");
  const [filterMonth, setFilterMonth] = useState(0); // 0 = tutti
  const [selectedMoonPhase, setSelectedMoonPhase] = useState("tutte");
  const [selectedDifficulty, setSelectedDifficulty] = useState("tutte");
  
  // Filtra ortaggi
  const filteredOrtaggi = useMemo(() => {
    return ortaggi
      .filter(ortaggio => {
        // Ricerca per nome
        if (searchTerm && !ortaggio.nome.toLowerCase().includes(searchTerm.toLowerCase())) {
          return false;
        }

        // Filtra per tipo
        if (selectedType !== "tutti" && ortaggio.tipo !== selectedType) {
          return false;
        }

        // Filtra per mese di semina
        if (filterMonth !== 0 && !ortaggio.mesiSemina.includes(filterMonth)) {
          return false;
        }

        // Filtra per fase lunare
        if (selectedMoonPhase !== "tutte" && ortaggio.lunaConsigliata.fase !== selectedMoonPhase) {
          return false;
        }

        // Filtra per difficoltà
        if (selectedDifficulty !== "tutte" && ortaggio.difficolta !== selectedDifficulty) {
          return false;
        }

        return true;
      })
      .sort((a, b) => a.nome.localeCompare(b.nome));
  }, [searchTerm, selectedType, filterMonth, selectedMoonPhase, selectedDifficulty]);
  
  return (
    <div className="min-h-screen flex flex-col">
      <SEO
        title="Calendario Semine con Fasi Lunari"
        description="Calendario delle semine per orto con fasi lunari. Scopri quando seminare ortaggi, verdure e piante. Guida completa per l'Umbria e centro Italia."
        canonical="/calendario-semine"
      />
      <BreadcrumbSchema items={[
        { name: "Home", url: "/" },
        { name: "Calendario Semine", url: "/calendario-semine" }
      ]} />
      <Navbar />
      
      {/* Header */}
      <section className="bg-gradient-to-br from-primary/20 via-background to-secondary/20 pt-24 pb-4">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center space-y-4">
            <h1 className="text-4xl md:text-5xl font-bold text-foreground">
              Calendario delle Semine
            </h1>
            <p className="text-lg text-muted-foreground">
              Scopri quando seminare i tuoi ortaggi seguendo le fasi lunari per un raccolto abbondante
            </p>

            {/* Fase Lunare Corrente - Versione compatta inline */}
            <div className="flex items-center justify-center sm:justify-center justify-end gap-2 pt-4 pr-4 sm:pr-0">
              <span className="text-sm text-muted-foreground">Fase lunare attuale:</span>
              <div className="inline-flex items-center gap-2 bg-gradient-to-br from-blue-900/20 to-purple-900/20 px-3 py-1.5 rounded-full border border-blue-800/30">
                <span className="text-xl">{getMoonPhase(new Date()).icon}</span>
                <span className="text-sm font-medium text-foreground">
                  {getMoonPhase(new Date()).name}
                </span>
              </div>
            </div>
          </div>
        </div>
      </section>
      
      {/* Main Content */}
      <section className="flex-1 py-6 bg-muted/30">
        <div className="container mx-auto px-2 sm:px-4 space-y-6">
          {/* Calendario Mensile */}
          <CalendarioMensile
            selectedMonth={selectedMonth}
            selectedYear={selectedYear}
            onMonthChange={(month, year) => {
              setSelectedMonth(month);
              setSelectedYear(year);
            }}
          />
          
          {/* Filtri */}
          <FiltriOrtaggi
            searchTerm={searchTerm}
            onSearchChange={setSearchTerm}
            selectedType={selectedType}
            onTypeChange={setSelectedType}
            selectedMonth={filterMonth}
            onMonthChange={setFilterMonth}
            selectedMoonPhase={selectedMoonPhase}
            onMoonPhaseChange={setSelectedMoonPhase}
            selectedDifficulty={selectedDifficulty}
            onDifficultyChange={setSelectedDifficulty}
          />

          {/* Lista Ortaggi */}
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h2 className="text-2xl font-bold text-foreground">
                Ortaggi {filterMonth !== 0 && `da seminare`}
              </h2>
              <p className="text-sm text-muted-foreground">
                {filteredOrtaggi.length} {filteredOrtaggi.length === 1 ? 'risultato' : 'risultati'}
              </p>
            </div>
            
            {filteredOrtaggi.length > 0 ? (
              <div className="grid gap-4">
                {filteredOrtaggi.map(ortaggio => (
                  <CardOrtaggio
                    key={ortaggio.id}
                    ortaggio={ortaggio}
                    zona={zona}
                  />
                ))}
              </div>
            ) : (
              <div className="bg-card border border-border rounded-lg p-12 text-center">
                <p className="text-muted-foreground">
                  Nessun ortaggio trovato con i filtri selezionati
                </p>
              </div>
            )}
          </div>
        </div>
      </section>

      <GuidaMensile />

      <Footer />
    </div>
  );
};

export default CalendarioSemine;
